<script setup lang="ts">
import { computed, onUnmounted, ref, toRef, watch } from 'vue'

import { useGetCreatorReports } from '@/api'
import { CreatorReportListItem, GenerateCreatorReportModal } from '@/components/CreatorDetail'
import { BoxComponent, ButtonComponent, FlexComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  userId: number
}>()

const POLL_INTERVAL_MS = 5000

const userIdRef = toRef(props, 'userId')
const { data: reports, isLoading, refresh } = useGetCreatorReports(userIdRef)

const isModalOpen = ref(false)

const hasPendingReports = computed(() => reports.value.some((report) => report.status === 'pending'))
const isInitialLoading = computed(() => isLoading.value && reports.value.length === 0)
const isEmpty = computed(() => !isLoading.value && reports.value.length === 0)

let pollTimer: ReturnType<typeof setInterval> | null = null

const stopPolling = () => {
  if (pollTimer !== null) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const startPolling = () => {
  if (pollTimer === null) {
    pollTimer = setInterval(() => void refresh(), POLL_INTERVAL_MS)
  }
}

watch(hasPendingReports, (hasPending) => (hasPending ? startPolling() : stopPolling()))

onUnmounted(stopPolling)

const onCreated = () => {
  isModalOpen.value = false
  void refresh()
}
</script>

<template>
  <BoxComponent :p="24">
    <FlexComponent justify="space-between" align="center" :gap="16">
      <TypographyComponent variant="title-large">Creator reports</TypographyComponent>
      <ButtonComponent variant="primary" @click="isModalOpen = true">Generate report</ButtonComponent>
    </FlexComponent>

    <BoxComponent :mt="24">
      <LoadingComponent v-if="isInitialLoading" :size="48" />
      <TypographyComponent v-else-if="isEmpty" variant="body-large" color="var(--secondary-48)">
        No reports yet. Generate one to get started.
      </TypographyComponent>
      <FlexComponent v-else direction="column" :gap="16">
        <CreatorReportListItem v-for="report in reports" :key="report.uuid" :report="report" />
      </FlexComponent>
    </BoxComponent>

    <GenerateCreatorReportModal v-if="isModalOpen" v-model="isModalOpen" :user-id="userId" @created="onCreated" />
  </BoxComponent>
</template>
